<template>
	<core-layout-content full>
		<core-layout-headline-and-actions>
			<template slot="headline">{{ $t("upload_participants") }}</template>
		</core-layout-headline-and-actions>

		<!-- File Upload -->
		<div class="flex xs-12 wrap">
			<div v-if="isInitial" class="dropbox bg5 xs-12 bc6 b3 p4">
				<input
					type="file"
					name="bulkUploadInput"
					:disabled="isProcessing"
					id="bulkUploadInput"
					class="input-file"
					accept=".xlsx, .xls, .csv"
					@change="readFile($event.target.files)"
				/>
				<p v-if="isInitial" class="c2 fw7 m4">
					{{ $t("drag_file") }}<br />{{ $t("click_to_browse") }}
				</p>
			</div>

			<!-- File Processing -->
			<div v-if="isProcessing" class="headline">
				{{ $t("processing", { fileName: fileName || $t("your_file") }) }}
			</div>

			<!-- Field Mapping -->
			<j-card class="mb6 bg5 p4 xs-12" v-if="isMapping">
				<div class="p4" v-if="sheetNames.length > 1">
					<j-base-select
						:options="sheetNames"
						class="xs-4"
						:placeholder="$t('select_sheet')"
						:value="selectedSheet"
						@input="val => (selectedSheet = val)"
						:allowSelectNothing="true"
					></j-base-select>
				</div>
				<div class="p4" v-if="sheetNames.length === 1 || selectedSheet">
					{{
						$t("record_preview", {
							previewLength:
								fileData[selectedSheetWithDefault].length - 1 > 4
									? "4"
									: fileData[selectedSheetWithDefault].length - 1,
							recordsLength: fileData[selectedSheetWithDefault].length - 1
						})
					}}
				</div>
			</j-card>
			<j-card v-if="isMapping" class="p4 bg5">
				<j-box-horizontal-scroll>
					<table class="table" v-if="sheetNames.length === 1 || selectedSheet">
						<thead>
							<tr>
								<th align="right"></th>
								<th v-for="(item, i) in header" :key="i" class="pr4 pb2 pt4">
									<j-base-select
										:options="
											fieldOptions.map(x => ({
												name: $t(x.name),
												value: x.value
											}))
										"
										:placeholder="$t('do_not_upload')"
										:value="invertedFieldMapping[i]"
										@input="val => updateFieldMapping(val, i)"
									></j-base-select>
								</th>
							</tr>
							<tr>
								<th align="right"></th>
								<th
									v-for="(item, i) in header"
									:key="`header-${i}`"
									class="pr4"
								>
									<div class="headline c6">
										{{ item.text }}
									</div>
								</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(item, i) in tableHead" :key="`tableHead-${i}`">
								<td class="pr2" align="right">{{ i + 1 }}</td>
								<td v-for="(column, i) in header" :key="i">
									{{ item[i] }}
								</td>
							</tr>
						</tbody>
					</table>
				</j-box-horizontal-scroll>
			</j-card>
			<div class="flex xs-12 xs-between mt6" v-if="isMapping">
				<j-button
					@click="batchUpload"
					:isDisabled="sheetNames.length !== 1 && !selectedSheet"
				>
					{{ $t("upload") }}
				</j-button>
				<j-button
					type="bordered"
					@click="$router.push({ name: 'InternalsList' })"
				>
					{{ $t("cancel") }}
				</j-button>
			</div>

			<!-- UPLOAD has Failed records -->
			<!-- TODO: allow user to download failed records for processing -->
			<j-card class="bg5 p4" v-if="isFailed">
				<div class="p4">
					{{
						$t("upload_failed", {
							successLength:
								fileData[selectedSheetWithDefault].length -
								1 -
								uploadError.length,
							failedLength: uploadError.length
						})
					}}
				</div>
				<j-box-horizontal-scroll>
					<table class="table">
						<thead>
							<tr>
								<th align="right"></th>
								<th v-for="(item, i) in header" :key="i">
									<div class="headline mt2 mb2 c6">
										{{ item.text }}
									</div>
								</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(item, i) in uploadError"
								:key="`uploadError-${i}`"
								class="cError fw9"
							>
								<td class="pr2 c6 fw5" align="right">{{ i + 1 }}</td>
								<td v-for="(column, i) in header" :key="i">
									{{ item.data[i] }}
								</td>
								<td v-if="item.errors && item.errors.length">
									{{ item.errors[0] }}
								</td>
							</tr>
						</tbody>
					</table>
				</j-box-horizontal-scroll>
			</j-card>
			<div class="flex xs-12 xs-between mt6" v-if="isFailed">
				<j-button @click="reset()">
					{{ $t("retry") }}
				</j-button>
				<j-button
					type="bordered"
					@click="$router.push({ name: 'InternalsList' })"
				>
					{{ $t("cancel") }}
				</j-button>
			</div>

			<!-- UPLOAD SUCCEEDED -->
			<j-card class="bg5" v-if="isSuccess">
				<div class="p4">
					<h3 class="headline cSuccess">
						{{
							$t("upload_success", {
								successLength: fileData[selectedSheetWithDefault].length - 1
							})
						}}
					</h3>
				</div>
			</j-card>
			<div class="flex xs-12 xs-right mt6" v-if="isSuccess">
				<j-button
					type="bordered"
					@click="$router.push({ name: 'InternalsList' })"
				>
					{{ $t("done") }}
				</j-button>
			</div>
		</div>
	</core-layout-content>
</template>

<script>
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import JBoxHorizontalScroll from "@/components/JBoxHorizontalScroll/"
import JBaseSelect from "@/components/JBaseSelect"
import JCard from "@/components/JCard/"
import JButton from "@/components/JButton/"
import XLSX from "xlsx"
import api from "../api/"

const STATUS_INITIAL = 0,
	STATUS_PROCESSING = 1,
	STATUS_MAPPING = 2,
	STATUS_PROCESSED = 3,
	STATUS_SUCCESS = 4,
	STATUS_FAILED = 5

export default {
	components: {
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		JBoxHorizontalScroll,
		JCard,
		JButton,
		JBaseSelect
	},
	data() {
		return {
			uploadedFile: [],
			uploadError: [],
			fileName: "",
			fileData: null,
			selectedSheet: null,
			currentStatus: null,
			mappingError: null,
			fieldMapping: {
				nameFormat: null,
				englishFirstName: null,
				englishLastName: null,
				chineseFirstName: null,
				chineseLastName: null,
				internalCode: null,
				isActive: null
			},
			fieldOptions: [
				{ name: "name_format", value: "nameFormat" },
				{ name: "english_first_name", value: "englishFirstName" },
				{ name: "english_last_name", value: "englishLastName" },
				{ name: "chinese_first_name", value: "chineseFirstName" },
				{ name: "chinese_last_name", value: "chineseLastName" },
				{ name: "internal_code", value: "internalCode" },
				{ name: "is_active", value: "isActive" }
			]
		}
	},
	computed: {
		invertedFieldMapping() {
			return Object.entries(this.fieldMapping).reduce((acc, cur) => {
				if (cur[1] !== null) acc[cur[1]] = cur[0]
				return acc
			}, [])
		},
		isInitial() {
			return this.currentStatus === STATUS_INITIAL
		},
		isMapping() {
			return this.currentStatus === STATUS_MAPPING
		},
		isProcessing() {
			return this.currentStatus === STATUS_PROCESSING
		},
		isSuccess() {
			return this.currentStatus === STATUS_SUCCESS
		},
		isProcessed() {
			return this.currentStatus === STATUS_PROCESSED
		},
		isFailed() {
			return this.currentStatus === STATUS_FAILED
		},
		header() {
			return this.fileData
				? this.fileData[this.selectedSheetWithDefault][0].map(item => ({
						text: item,
						label: item
				  }))
				: null
		},
		tableHead() {
			return this.fileData
				? this.fileData[this.selectedSheetWithDefault].slice(1, 5)
				: null
		},
		batchedTable() {
			function chunk(array, size) {
				if (!array) return []
				const firstChunk = array.slice(0, size) // create the first chunk of the given array
				if (!firstChunk.length) {
					return array // this is the base case to terminal the recursive
				}
				return [firstChunk].concat(chunk(array.slice(size, array.length), size))
			}
			return this.fileData
				? chunk(this.fileData[this.selectedSheetWithDefault].slice(1), 20)
				: []
		},
		sheetNames() {
			return this.fileData
				? Object.keys(this.fileData).map(name => ({ value: name, name }))
				: []
		},
		selectedSheetWithDefault() {
			return (
				this.selectedSheet ||
				(this.sheetNames.length ? this.sheetNames[0].value : null)
			)
		}
	},
	mounted() {
		this.reset()
	},
	methods: {
		reset() {
			this.currentStatus = STATUS_INITIAL
			this.uploadedFile = []
			this.fileName = ""
			this.fileData = null
			this.selectedSheet = null
			this.mappingError = null
			this.uploadError = []
			this.fieldMapping = Object.keys(this.fieldMapping).reduce(
				(resetMap, k) => {
					resetMap[k] = null
					return resetMap
				},
				{}
			)
		},
		updateFieldMapping(field, dataColumn) {
			let oldField =
				this.invertedFieldMapping[dataColumn] === field
					? field
					: this.invertedFieldMapping[dataColumn]
			this.fieldMapping = {
				...this.fieldMapping,
				...{ [oldField]: null },
				[field || oldField]: field ? dataColumn : null
			}
		},
		readFile(fileList) {
			this.currentStatus = STATUS_PROCESSING
			this.fileName = fileList[0].name
			const reader = new FileReader()
			reader.onload = e => {
				let result = {}
				const data = e.target.result
				let wb = XLSX.read(data, { type: "array" })
				wb.Workbook.Sheets.forEach(sheet => {
					if (sheet.Hidden === 0) {
						const roa = XLSX.utils
							.sheet_to_json(wb.Sheets[sheet.name], {
								header: 1
							})
							.filter(row => row.length)
						if (roa.length) result[sheet.name] = roa
					}
				})
				this.fileData = result
				this.currentStatus = STATUS_MAPPING
			}
			reader.readAsArrayBuffer(fileList[0])
		},
		async upload(payload) {
			let mappedPayload = Object.entries(this.fieldMapping).reduce(
				(result, [k, v]) => {
					if (typeof v === "number") result[k] = payload[v] || null
					return result
				},
				{}
			)
			if (
				!mappedPayload.internalCode ||
				mappedPayload.internalCode.trim() === ""
			)
				delete mappedPayload.internalCode
			const res = await api.createInternal({
				createInternalInput: { ...mappedPayload }
			})
			let result = {
				status: res?.data?.createInternal?.status,
				errors: res?.errors,
				data: payload
			}
			if (res.errors) {
				this.uploadError.push(result)
			}
			return result
		},
		async batchUpload() {
			// validate before upload
			let mappingError =
				Object.values(this.fieldMapping).filter(x => typeof x === "number")
					.length === 0 // at least one field mapped
			if (mappingError) {
				this.$store.dispatch("addMessage", {
					message: { type: "error", text: this.$t("error_minimum_one") }
				})
				return
			}
			this.currentStatus = STATUS_PROCESSING
			// TODO: add progress indicator
			for (const batch of this.batchedTable) {
				// doing uploads in batches of 20
				const result = await Promise.all(batch.map(b => this.upload(b)))
			}
			this.currentStatus = this.uploadError.length
				? STATUS_FAILED
				: STATUS_SUCCESS
		}
	}
}
</script>

<style scoped>
.dropbox {
	border-style: dashed;
	min-height: 200px; /* minimum height */
	cursor: pointer;
}

.input-file {
	opacity: 0;
	width: 100%;
	height: 200px;
	position: absolute;
	cursor: pointer;
}

.dropbox:hover {
	background: var(--color4);
}

.dropbox p {
	font-size: 1.2em;
	text-align: center;
	padding: 50px 0;
}

.table {
	width: 100%;
	border-collapse: collapse;
}

thead > tr > th {
	position: relative;
	height: 48px;
	text-align: left;
	min-width: 150px;
	white-space: nowrap;
	padding-right: 16px;
	border-bottom: 1px solid var(--color7);
}

thead > tr > th > .headline {
	height: 100%;
	display: -webkit-inline-box;
	display: -moz-inline-box;
	display: -ms-inline-flexbox;
	display: -webkit-inline-flex;
	display: inline-flex;
	justify-content: flex-start;
	align-items: center;
	padding: 0 var(--spacing2);
	font-weight: var(--fontWeight7);
	font-size: var(--fontSize3);
	text-align: left;
	color: var(--color6);
}

thead > tr > th.sorting {
	cursor: pointer;
}

thead > tr > th.sorting:hover {
	background-color: var(--color4);
}

thead > tr > th.active .headline {
	color: var(--color1);
}

thead > tr > th > .material-icons {
	position: absolute;
	top: 0;
	justify-content: center;
	align-items: center;
	width: 18px;
	height: 100%;
	transition: all 160ms ease-out;
}

thead > tr > th.desc > .material-icons {
	transform: rotate(180deg);
}

thead > tr > th:first-child {
	padding-left: var(--spacing2);
	min-width: 0px;
	text-align: right;
}

thead > tr > th:last-child {
	padding-right: var(--spacing2);
}

thead > tr > th.sorting:last-child {
	padding-right: var(--spacing6);
}

tbody > tr {
	border-bottom: 1px solid var(--color7);
}

tbody > tr:last-child {
	border-bottom: 0;
}

tbody > tr > td {
	height: 48px;
	font-size: var(--fontSize3);
	line-height: var(--lineHeight125);
	white-space: nowrap;
	padding: 0 var(--spacing10) 0 var(--spacing2);
}

tbody tr:hover {
	background-color: var(--color4);
}

tbody > tr > td:first-child {
	padding-left: var(--spacing4);
	color: grey;
}

tbody > tr > td:last-child {
	padding-right: var(--spacing4);
}

tbody > tr > td > a {
	display: flex;
	justify-content: flex-start;
	align-items: center;
	height: 100%;
	text-decoration: none;
	color: var(--color2);
}

tbody > tr > td > .action {
	display: inline-flex;
	justify-content: center;
	align-items: center;
	height: 100%;
	width: 32px;
	cursor: pointer;
}

tbody > tr > td > .action:hover {
	opacity: 0.7;
}

/* Modifier for td - paragraph */
tbody > tr > td.paragraph {
	white-space: normal;
	padding-top: var(--spacing2);
	padding-bottom: var(--spacing2);
	line-height: var(--lineHeight125);
}

tbody.invisible tr {
	opacity: 0;
	pointer-events: none;
	border-color: transparent;
}
</style>
